import {
    useCallback, useState, useEffect, useContext
} from 'react';
import _ from 'lodash';
import moment from 'moment';
import { withRouter } from 'react-router-dom';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import PropTypes from 'prop-types';
import { useAuthentication, withAuthenticationContext } from 'gw-digital-auth-react';
import { BillingService } from 'gw-capability-billing';
import { AgentService, PolicyService } from 'gw-capability-policy';
import { withApplicationPropertyContext } from 'tdic-components-platform-react';
import { TermsAndConditionUtils, TimeUtil, DateUtil } from 'gw-portals-util-js';
import { ModalNextProvider } from '@jutro/components';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import { error } from '@jutro/logger';
import metadata from './ServiceRequest.metadata.json5';
import messages from './ServiceRequest.messages';
import styles from './ServiceRequest.module.scss';

function ServiceRequest(props) {
    const {
        tdic: {
            footerInformation: {
                externalLink
            }
        }
    } = appConfig;
    const {
        tncTransactionTypesSubTypes, loggedUserContact,
        user_name: userName, email, history, termData: terms
    } = props;
    const [formData, setFormData] = useState({ checkBoxDetails: false });
    const [selectPolicy, setSelectedPolicy] = useState([]);
    const [selectedPolicyResponse, setPolicyResponse] = useState({});
    const { authHeader } = useAuthentication();
    const translator = useContext(TranslatorContext);
    const intl = useContext(IntlContext);
    const [selectedDropdownValue, setDropdownValue] = useState('');
    const [selectedRadioButtonValue, setRadioButtonValue] = useState({});
    const [tncPolicyNumber, setTncPolicyNumber] = useState('');
    const [policyOfferingCode, setOfferingCode] = useState('');
    const [acceptTime, setTime] = useState('');
    const [policyTypes, setPolicyTypes] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [holidaysLoaded, setHolidaysLoaded] = useState(false);
    const CONTACT_HOURS = {
        MIN_START_TIME: 8,
        MAX_START_TIME: 16,
        MIN_END_TIME: 9,
        MAX_END_TIME: 17
    };
    const DISABLED_DAYS_OF_WEEK = [
        0, // Sunday
        6 //  Saturday
    ];

    useEffect(() => {
        const offeringsPromise = PolicyService.getPolicyAndOfferings(authHeader)
            .then((response) => {
                setSelectedPolicy(response);
            });
        const holidaysPromise = BillingService
            .getHolidaysOnOrAfterRequestDate(DateUtil.currentDate(), authHeader)
            .then((data) => {
                const mappedData = _.map(_.map(data, 'date'), (dto) => {
                    const { year, day } = dto;
                    const month = `${dto.month + 1}`.padStart(2, '0');
                    return moment(`${year}-${month}-${day}`, 'YYYY-MM-DD');
                });
                setHolidays(mappedData);
            });
        Promise
            .all([offeringsPromise, holidaysPromise])
            .then(() => setHolidaysLoaded(true))
            .catch((err) => {
                error(`Unable to update Service Request ${JSON.stringify(err)}`);
                return ModalNextProvider.showAlert({
                    title: messages.serviceRequestFailed,
                    message: messages.canNotLoadServiceRequest,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: messages.ok
                }).then(() => {
                    history.push('/error');
                }, _.noop);
            });
    }, [authHeader, history]);
    // Call once when page is mount
    // eslint-disable-next-line react-hooks/exhaustive-deps

    const checkValidBusinessDate = (date) => {
        const dateToCheck = moment(date);
        // Check if weekend
        if (DISABLED_DAYS_OF_WEEK.includes(dateToCheck.day())) {
            return false;
        }
        // Check if holiday
        return _.find(holidays, (d) => dateToCheck.isSame(d, 'day')) === undefined;
    };

    const endTimeIsEightBusHoursLaterThanStartTime = (startTime, endTime) => {
        // Clone the start time so that we don't modify the original object
        let momentTime = moment(startTime).clone().add(1, 'hours');
        const momentET = moment(endTime);
        const validHours = [];
        const { MIN_START_TIME, MAX_START_TIME } = CONTACT_HOURS;
        for (let i = MIN_START_TIME; i <= MAX_START_TIME; i += 1) {
            validHours.push(i);
        }
        let numBusHours = 0;
        while (momentTime.isBefore(momentET)) {
            if (checkValidBusinessDate(momentTime)) {
                if (validHours.includes(momentTime.hours())) {
                    numBusHours += 1;
                }
                if (numBusHours >= 8) {
                    return true;
                }
            }
            momentTime = momentTime.add(1, 'hours');
        }
        return false;
    };

    // Returns the next business datetime rounded up to the next hour
    const earliestBestTimeDate = useCallback(() => {
        const curDate = DateUtil.currentDate();
        // Doing this so that we don't have to worry about
        // clearing out all the smaller fields and only have
        // to worry about hours.
        let dateToReturn = moment(DateUtil.trimToMidnight(curDate))
            .set('hour', curDate.getHours());
        const currentHour = dateToReturn.hours();
        const startTimeValidBusDay = checkValidBusinessDate(curDate);
        // If it's already later than CONTACT_HOURS.MAX_START_TIME OR if
        // startTimeValidBusDay is false, then we need to move the date
        // to the next day and then set the hour to MIN_START_TIME.
        // Otherwise, set the hour to the next hour.
        if (!startTimeValidBusDay || currentHour >= CONTACT_HOURS.MAX_START_TIME) {
            dateToReturn = dateToReturn
                .add(1, 'days')
                .set('hours', 8);
        } else if (currentHour < CONTACT_HOURS.MIN_START_TIME) {
            dateToReturn = dateToReturn.set('hours', 8);
        } else {
            dateToReturn = dateToReturn.set('hours', currentHour + 1);
        }
        // Keep copy of the time we started at so that we can make sure dateToReturn
        // ends up being at least eight business hours from startTime.
        const startTime = dateToReturn.clone();

        const dateValid = () => {
            return checkValidBusinessDate(dateToReturn)
                && endTimeIsEightBusHoursLaterThanStartTime(startTime, dateToReturn);
        };
        // Check if the current value of dateToReturn is:
        //   1. A valid business date.
        //   2. Is at least 8 business hours after startTime.
        // If either is false, add a day to dateToReturn and recheck.
        while (!dateValid()) {
            dateToReturn = dateToReturn.add(1, 'days');
        }

        return dateToReturn.toDate();
    }, [
        holidaysLoaded,
        CONTACT_HOURS,
        endTimeIsEightBusHoursLaterThanStartTime,
        checkValidBusinessDate
    ]);

    const extractHourFromBestTime = (timeStr) => {
        if (timeStr && !_.isEmpty(timeStr)) {
            const timeMatches = timeStr.match(/([\d]+):([\d]+)\s+([A|P]M)/);
            if (timeMatches.length === 4) {
                let timeHour = parseInt(timeMatches[1], 10);
                if (timeHour !== 12 && timeMatches[3] === 'PM') {
                    timeHour += 12;
                }
                return timeHour;
            }
        }
        return null;
    };

    const validateTime = useCallback(() => {
        if (_.isEmpty(formData.selectedDate)) {
            return true;
        }
        if (!checkValidBusinessDate(formData.selectedDate)) {
            return false;
        }
        if (_.isEmpty(formData.endTime)) {
            return true;
        }
        const selectedStartDate = moment(`${formData.selectedDate.replace(/-/g, '/')} ${formData.startTime}`);
        const selectedEndDate = moment(`${formData.selectedDate.replace(/-/g, '/')} ${formData.endTime}`);
        const earliestDate = moment(earliestBestTimeDate());
        if (earliestDate.isSame(selectedStartDate, 'day')) {
            return earliestDate.isSameOrBefore(selectedStartDate)
                && selectedStartDate.isBefore(selectedEndDate);
        }
        return selectedStartDate.isBefore(selectedEndDate);
    }, [formData]);

    const setDateValueEmpty = useCallback(
        (value, path) => {
            const dateClone = _.cloneDeep(acceptTime);
            _.set(formData, 'startTime', '');
            _.set(formData, 'endTime', '');
            _.set(dateClone, path, value);
            setTime(dateClone);
        },
        [acceptTime, formData]
    );

    const onRadioButtonValueChange = useCallback(
        (value, path) => {
            const dataClone = _.cloneDeep(selectedRadioButtonValue);
            setDropdownValue('');
            _.set(formData, 'propertyLocation', '');
            _.set(formData, 'checkBoxDetails', '');
            _.set(formData, 'selectType', '');
            _.set(formData, 'requestReason', '');
            _.set(formData, 'requestDetails', '');
            _.set(formData, 'selectedDate', '');
            _.set(selectedPolicyResponse, 'typeOfChanges', '');
            _.set(formData, 'contactMethod', '');
            setDateValueEmpty();
            _.set(dataClone, path, value);
            setRadioButtonValue(dataClone);
        },
        [formData, selectedRadioButtonValue, selectedPolicyResponse, setDateValueEmpty]
    );

    const loadPolicyTypes = useCallback(
        (selectedPolicy) => {
            const listOfChanges = _.get(selectedPolicy, 'typeOfChanges');
            let listOfContacts = [];
            listOfContacts = listOfChanges && listOfChanges.map((data) => {
                return {
                    code: data,
                    name: translator({
                        id: data,
                        defaultMessage: data
                    })
                };
            });
            if (!_.isEmpty(listOfContacts)) {
                if (listOfContacts.length > 1) {
                    const otherContactIndex = listOfContacts.findIndex(
                        (contact) => {
                            return contact.code === 'Other';
                        }
                    );
                    if (otherContactIndex) {
                        const [otherContact] = listOfContacts.splice(otherContactIndex, 1);
                        listOfContacts.push(otherContact);
                    }
                } else if (listOfContacts.length === 1) {
                    const [type] = listOfContacts;
                    _.set(formData, 'selectType', type.code);
                }
            }
            setPolicyTypes(listOfContacts);
            return listOfContacts;
        }, [formData, translator]
    );

    const onDropdownValueChange = useCallback(
        (value) => {
            _.set(formData, 'selectType', '');
            setDropdownValue(value);
            const splittedValue = value.split(' ');
            const selectedData = {
                SelectedPolicyNumber: splittedValue[1],
                OfferingCode: splittedValue[0]
            };
            setTncPolicyNumber(selectedData.SelectedPolicyNumber);
            setOfferingCode(selectedData.OfferingCode);
            PolicyService.getPolicyLocationsAndChangeReasons(selectedData,
                authHeader).then((response) => {
                setPolicyResponse(response);
                loadPolicyTypes(response);
            });
        },
        [authHeader, loadPolicyTypes, formData]
    );

    const propertyLocationValid = useCallback(() => {
        const locationValue = (policyOfferingCode === 'PLClaimsMade_TDIC'
            || policyOfferingCode === 'PLCyberLiab_TDIC' || policyOfferingCode === 'PLOccurence_TDIC');
        if (locationValue) {
            _.set(formData, 'propertyLocation', ' ');
        }
        return formData.propertyLocation;
    }, [formData, policyOfferingCode]);

    const checkValidation = useCallback(() => {
        if (selectedRadioButtonValue.radioButtonCode === 'policyChange') {
            if (!_.isEmpty(selectedDropdownValue && formData.selectType.length
                && propertyLocationValid() && formData.requestReason
                && formData.requestDetails && formData.selectedDate && formData.startTime
                && formData.endTime && formData.contactMethod)) {
                return true;
            }
        } else if (!_.isEmpty(selectedDropdownValue && formData.requestReason
            && formData.requestDetails && formData.selectedDate && formData.startTime
            && formData.endTime && formData.contactMethod)) {
            return true;
        }
        return false;
    }, [formData, propertyLocationValid, selectedRadioButtonValue, selectedDropdownValue]);

    const onValueChange = useCallback((value, path) => {
        const formDataClone = _.cloneDeep(formData);
        let shouldValidateTime = false;
        if (path === 'endTime') {
            shouldValidateTime = true;
        } else if (path === 'startTime') {
            const startTimeHour = extractHourFromBestTime(value);
            const endTimeHour = extractHourFromBestTime(formDataClone.endTime);
            if (startTimeHour >= endTimeHour) {
                // Clear out endTime so that the validation message doesn't trigger.
                _.set(formDataClone, 'endTime', null);
            } else {
                shouldValidateTime = true;
            }
        } else if (path === 'selectedDate') {
            const startTimeHour = extractHourFromBestTime(formDataClone.startTime);
            const earliestDate = moment(earliestBestTimeDate());
            const dateValue = moment(value);
            // If the new selected date is the same day as the earliestBestTimeDate
            // and the earliestBestTimeDate hour is after the current startTimeHour
            // then we need to clear out the selected start and end times to stop
            // the validation message from triggering.
            if (dateValue.isSame(earliestDate, 'days')) {
                if (startTimeHour && startTimeHour < earliestDate.hours()) {
                    _.set(formDataClone, 'startTime', null);
                    _.set(formDataClone, 'endTime', null);
                }
            }
        }
        if (shouldValidateTime) {
            validateTime();
        }
        _.set(formDataClone, path, value);
        setFormData(formDataClone);
    }, [validateTime, formData, extractHourFromBestTime]);

    const handlePrint = useCallback(() => {
        window.onafterprint = () => {
            setTimeout(() => { history.push('/home'); }, 0);
        };
        window.print();
    }, []);

    const getAvailableTime = useCallback((endHour) => {
        const earliestDate = earliestBestTimeDate();
        const selectedDate = moment(formData.selectedDate, 'YYYY-MM-DD').toDate();
        let startHour = (endHour === CONTACT_HOURS.MAX_START_TIME)
            ? CONTACT_HOURS.MIN_START_TIME
            : CONTACT_HOURS.MIN_END_TIME;
        // If the earliest date is the same day as the selected date, we have to
        // filter the hours down to be after the earliest time.
        if (DateUtil.compareIgnoreTime(earliestDate, selectedDate) === 0) {
            startHour = earliestDate.getHours();
            if (endHour === CONTACT_HOURS.MAX_END_TIME) {
                startHour += 1;
            }
        }
        // If the start time is already filled out (and we're constructing
        // the values for the end time), change startHour to be startTime instead.
        if (formData.startTime && endHour === CONTACT_HOURS.MAX_END_TIME) {
            const startTimeHour = extractHourFromBestTime(formData.startTime);
            if (startTimeHour) {
                if (startTimeHour >= startHour) {
                    // Add 1 so that we have an hour gap at least.
                    startHour = startTimeHour + 1;
                }
            }
        }
        const value = TimeUtil.getAvailableTime(startHour, endHour, 60);
        let timeDropdownOptions = [];
        timeDropdownOptions = value.map((data) => {
            return {
                code: data,
                name: data
            };
        });
        return timeDropdownOptions;
    }, [formData, CONTACT_HOURS, extractHourFromBestTime]);

    const getPropertyLocationValue = useCallback(
        () => {
            const listOfChanges = _.get(selectedPolicyResponse, 'propertyLocation');
            let listOfContacts = [];
            listOfContacts = listOfChanges && listOfChanges.map((data) => {
                return {
                    code: data.address.displayName,
                    name: translator({
                        id: data.address.displayName,
                        defaultMessage: data.address.displayName
                    })
                };
            });
            return listOfContacts;
        }, [selectedPolicyResponse, translator]
    );

    const saveTermsAndConditions = useCallback(() => {
        let transactionSubTypes = '';
        if (selectedRadioButtonValue.radioButtonCode === 'policyChange') {
            transactionSubTypes = 'PolicyChange';
        }
        if (selectedRadioButtonValue.radioButtonCode === 'thirdParty') {
            transactionSubTypes = 'ThirdPartyConsultation';
        }
        if (selectedRadioButtonValue.radioButtonCode === 'claimsHistory') {
            transactionSubTypes = 'ClaimHistory';
        }
        if (selectedRadioButtonValue.radioButtonCode === 'insuranceReview') {
            transactionSubTypes = 'InsuranceReview';
        }
        if (selectedRadioButtonValue.radioButtonCode === 'otherQuestions') {
            transactionSubTypes = 'Other';
        }
        return TermsAndConditionUtils.saveTnCAcceptance(_.get(terms, 'Version', ''), tncTransactionTypesSubTypes,
            'ServiceRequest', transactionSubTypes, authHeader, tncPolicyNumber, userName, email);
    }, [authHeader, email, selectedRadioButtonValue.radioButtonCode, terms, tncPolicyNumber,
        tncTransactionTypesSubTypes, userName]);

    const onSubmit = useCallback(() => {
        const name = `${_.get(loggedUserContact, 'accountContact.firstName', '')} ${_.get(loggedUserContact, 'accountContact.lastName', '')}`;
        const selectType = Array.isArray(formData.selectType) ? formData.selectType
            : [formData.selectType];

        const data = {
            serviceRequestTypeCode: selectedRadioButtonValue.radioButtonCode,
            policyNumber: tncPolicyNumber,
            location: formData.propertyLocation,
            typeOfPolicyChange: selectType,
            reasonForRequest: formData.requestReason,
            detailsOfRequest: formData.requestDetails,
            requestedBy: name,
            bestDayToContactMe: intl.formatDate(
                moment(formData.selectedDate, 'YYYY-MM-DD').toDate(),
                { year: 'numeric', month: 'short', day: 'numeric' }
            ),
            bestStartTimeToContactMe: formData.startTime,
            bestEndTimeToContactMe: formData.endTime,
            preferredContactMethod: formData.contactMethod
        };

        AgentService.sendServiceRequestEmailToAgent(data, authHeader).then((result) => {
            if (result) {
                saveTermsAndConditions();
                return ModalNextProvider.showConfirm({
                    title: messages.modalTitle,
                    message: translator(messages.tdicReference),
                    status: 'success',
                    icon: 'mi-error-outline',
                    cancelButtonText: messages.printRequest,
                    confirmButtonText: messages.backHomePage
                }).then(async (results) => {
                    if (results === 'cancel') {
                        handlePrint();
                    } else {
                        return history.push('/home');
                    }
                    return true;
                }, _.noop);
            }
            return ModalNextProvider.showAlert({
                title: messages.errorTitle,
                message: messages.errorMessage,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: messages.errorMessageCancel
            }).catch(_.noop);
        });
    }, [authHeader, formData.contactMethod, formData.endTime, formData.propertyLocation,
        formData.requestDetails, formData.requestReason, formData.selectType, formData.selectedDate,
        formData.startTime, handlePrint, history, intl, loggedUserContact,
        saveTermsAndConditions, selectedDropdownValue, selectedRadioButtonValue.radioButtonCode,
        tncPolicyNumber, translator]);

    const onCancelClick = useCallback(() => {
        return history.push('/home');
    }, [history]);

    if (_.isUndefined(selectPolicy)) {
        return null;
    }

    const errorMessage = () => {
        if (!checkValidBusinessDate(formData.selectedDate)) {
            return messages.noWeekendsErrorMessage;
        }
        return messages.errorNotificationMessage;
    };

    const isVisible = (policyOfferingCode === 'PLClaimsMade_TDIC' || policyOfferingCode === 'PLCyberLiab_TDIC'
        || policyOfferingCode === 'PLOccurence_TDIC');
    const isValid = checkValidation();
    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            showOptional: true
        },
        selectPolicy: {
            availableValues: selectPolicy.map((data) => ({
                code: (`${data.offeringCode} ${data.policyNumber}`),
                name: (`${data.offeringName} - ${data.policyNumber}`),
            })),
            value: selectedDropdownValue
        },
        propertyLocation: {
            visible: !_.isEmpty(selectedRadioButtonValue.radioButtonCode) && selectedRadioButtonValue.radioButtonCode === 'policyChange'
                && !isVisible && !_.isEmpty(selectedPolicyResponse.typeOfChanges),
            availableValues: getPropertyLocationValue(),
            value: formData.propertyLocation
        },
        reasonOfRequest: {
            value: formData.requestReason
        },
        detailsOfRequest: {
            value: formData.requestDetails
        },
        termsOfUseCheckbox: {
            showOptional: false,
            value: formData.checkBoxDetails,
            label: messages.termsOfUse
        },
        changePolicyContainer: {
            visible: !_.isEmpty(selectedRadioButtonValue.radioButtonCode),
        },
        selectDateAndTime: {
            value: formData.selectedDate,
            minDate: DateUtil.trimToMidnight(earliestBestTimeDate()),
            disabled: !holidaysLoaded
        },
        termsOfConditions: {
            href: _.get(externalLink, 'termsOfUse')
        },
        accountHolder: {
            value: _.get(loggedUserContact, 'accountContact.displayName')
        },
        serviceSelection: {
            value: selectedRadioButtonValue.radioButtonCode
        },
        selectType: {
            visible: selectedRadioButtonValue.radioButtonCode === 'policyChange' && !_.isEmpty(selectedPolicyResponse.typeOfChanges),
            availableValues: policyTypes,
            value: formData.selectType
        },
        submitButton: {
            disabled: !(formData.checkBoxDetails && isValid && validateTime()),
        },
        startTime: {
            availableValues: getAvailableTime(CONTACT_HOURS.MAX_START_TIME),
            value: formData.startTime,
            disabled: _.isEmpty(formData.selectedDate)
        },
        endTime: {
            availableValues: getAvailableTime(CONTACT_HOURS.MAX_END_TIME),
            value: formData.endTime,
            disabled: _.isEmpty(formData.selectedDate) || _.isEmpty(formData.startTime)
        },
        errorMsg: {
            visible: !validateTime(),
            message: errorMessage()
        },
        contactMethod: {
            value: formData.contactMethod
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onValueChange: onValueChange,
            onSubmit: onSubmit,
            onDropdownValueChange: onDropdownValueChange,
            onRadioButtonValueChange: onRadioButtonValueChange,
            onCancelClick: onCancelClick
        }
    };

    return renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers);
}

ServiceRequest.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired
};

export default withRouter(withAuthenticationContext(
    withApplicationPropertyContext(ServiceRequest)
));
